// SortableHeader — a clickable <th> that drives server-side sort.
//
// Pairs with the useSort() hook: the parent list page keeps { col, dir }
// state, injects it into the query key + queryFn, and renders each column
// header with <SortableHeader col="name" sort={sort} label="Name" />.
// Clicking a column that's already active flips direction; a new column
// starts in the default direction (asc for text, desc for timestamps —
// caller picks via defaultDir).

// The column names here are the API's `?sort=` keys, not the SQL columns —
// see lib/sort.js safelists on the worker side for the mapping.

import { useState } from 'react';

export function useSort(initial = { col: null, dir: 'asc' }) {
  const [state, setState] = useState(initial);
  const toggle = (col, defaultDir = 'asc') => {
    setState((prev) => {
      if (prev.col !== col) return { col, dir: defaultDir };
      return { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };
  return { ...state, toggle, set: setState };
}

function SortIndicator({ active, dir }) {
  if (!active) {
    return (
      <span className="ml-1 inline-block text-gray-300" aria-hidden="true">↕</span>
    );
  }
  return (
    <span className="ml-1 inline-block text-blue-600" aria-hidden="true">
      {dir === 'asc' ? '▲' : '▼'}
    </span>
  );
}

export default function SortableHeader({ col, sort, label, defaultDir = 'asc', className = '' }) {
  const active = sort?.col === col;
  return (
    <th
      className={`cursor-pointer select-none px-4 py-2 text-left text-xs font-medium text-gray-500 hover:text-gray-700 ${className}`}
      onClick={() => sort?.toggle?.(col, defaultDir)}
    >
      <span className="inline-flex items-center">
        {label}
        <SortIndicator active={active} dir={sort?.dir} />
      </span>
    </th>
  );
}
